import React, {useEffect, useState} from 'react';

const SCORE_TOOLTIP =
  'Calculated using skills overlap, role seniority, and profile depth.';

function getScoreBarClasses(score: number): string {
  if (score >= 75) return 'bg-green-500';
  if (score >= 60) return 'bg-blue-500';
  return 'bg-gray-400';
}

function getScoreBgClasses(score: number): string {
  if (score >= 75) return 'bg-green-50';
  if (score >= 60) return 'bg-blue-50';
  return 'bg-gray-100';
}

interface AuraScoreBarProps {
  auraScore: number;
  requiredSkillsMet?: number;
  requiredSkillsTotal?: number;
  missingSkillNames?: string[];
  yearsExperience?: number;
}

export const AuraScoreBar: React.FC<AuraScoreBarProps> = ({
  auraScore,
  requiredSkillsMet,
  requiredSkillsTotal,
  missingSkillNames,
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    setAnimatedScore(auraScore);
  }, [auraScore]);

  const barClass = getScoreBarClasses(auraScore);
  const bgClass = getScoreBgClasses(auraScore);

  const missingCount = missingSkillNames?.length ?? 0;
  const microCopy =
    missingCount > 0
      ? missingCount === 1
        ? '1 core skill missing'
        : `${missingCount} core skills missing`
      : requiredSkillsTotal != null && requiredSkillsTotal > 0 && requiredSkillsMet != null
        ? `✔ ${requiredSkillsMet}/${requiredSkillsTotal} required`
        : '';

  return (
    <div
      className={`w-full px-2 py-1.5 rounded-lg flex flex-col items-center gap-1 ${bgClass}`}
      title={SCORE_TOOLTIP}>
      <div className="flex items-center justify-between w-full">
        <span className="text-gray-700 text-xs font-medium">{auraScore}% Match</span>
      </div>
      <div className="w-full">
        <div className="h-1.5 bg-gray-200 rounded-full relative overflow-hidden">
          <div
            className={`h-1.5 rounded-full absolute top-0 left-0 transition-all duration-500 ease-out ${barClass}`}
            style={{width: `${animatedScore}%`}}
          />
        </div>
      </div>
      {microCopy ? (
        <p className="text-gray-500 text-[10px] w-full text-center" title={SCORE_TOOLTIP}>
          {microCopy}
        </p>
      ) : null}
    </div>
  );
};
